import React, { useState, useMemo, useRef, useEffect } from "react";
import { formsGuidelinesApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./FormsGuidelines.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = ["Admission", "Examination", "Scholarship", "Hostel", "Leave", "Other"];
const AUDIENCES  = ["Students", "Faculty", "Staff", "Parents", "Public"];

const TABS = [
  { key: "all",         label: "All Documents",    icon: "📂" },
  { key: "Admission",   label: "Admission",        icon: "🎓" },
  { key: "Examination", label: "Examination",      icon: "📝" },
  { key: "Scholarship", label: "Scholarships",     icon: "💰" },
  { key: "Academic",    label: "Academic",         icon: "📚" },
  { key: "Hostel",      label: "Hostel & Facilities", icon: "🏠" },
];

const BLANK_FORM = {
  _id: null,
  Form_Title: "",
  Category: "Admission",
  For_Audience: "Students",
  Description: "",
  File_URL: "",
  File_Type: "PDF",
  File_Size: "",
  Version: "1.0",
  Academic_Year: "2025-26",
  Last_Updated: new Date().toISOString().split('T')[0],
  Status: "Active",
  Mark_as_Important: false,
  Pin_to_Top: false,
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FormsGuidelines = () => {
  const [forms, setForms]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [activeTab, setActiveTab]   = useState("all");
  const [search, setSearch]         = useState("");
  const [modal, setModal]           = useState(""); // "add" | "edit" | "view"
  const [form, setForm]             = useState(BLANK_FORM);
  const [viewItem, setViewItem]     = useState(null);
  
  const [dropdown, setDropdown]     = useState({ id: null, top: 0, left: 0 });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchForms();
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) closeDD(); };
    const onScroll = () => closeDD();
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, []);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const res = await apiClient(formsGuidelinesApi);
      const json = await res.json();
      if (res.ok) setForms(json.data || []);
    } catch (err) {
      console.error("Error fetching forms:", err);
    } finally {
      setLoading(false);
    }
  };

  const closeDD = () => setDropdown({ id: null, top: 0, left: 0 });
  const openDD  = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdown({ id, top: rect.bottom + 8, left: rect.right - 210 });
  };

  const openAdd  = () => { setForm({ ...BLANK_FORM, Last_Updated: new Date().toISOString().split('T')[0] }); setModal("add"); };
  const openEdit = (item) => { 
    setForm({ ...item, Last_Updated: item.Last_Updated ? new Date(item.Last_Updated).toISOString().split('T')[0] : "" }); 
    setModal("edit"); 
    closeDD(); 
  };
  const openView = (item) => { setViewItem(item); setModal("view"); closeDD(); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const isAdd = modal === "add";
      const method = isAdd ? "POST" : "PUT";
      const url = isAdd ? formsGuidelinesApi : `${formsGuidelinesApi}/${form._id}`;
      const res = await apiClient(url, {
        method,
        body: JSON.stringify(form)
      });
      if (res.ok) {
        fetchForms();
        setModal("");
      }
    } catch (err) {
      console.error("Error saving document:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const res = await apiClient(`${formsGuidelinesApi}/${id}`, { method: "DELETE" });
      if (res.ok) {
        fetchForms();
        setConfirmDelete(null);
      }
    } catch (err) {
      console.error("Error deleting document:", err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return forms.filter(f => {
      const matchTab = activeTab === "all" || f.Category === activeTab;
      const matchSearch = !search || f.Form_Title.toLowerCase().includes(search.toLowerCase()) || f.Description.toLowerCase().includes(search.toLowerCase());
      return matchTab && matchSearch;
    });
  }, [forms, activeTab, search]);

  const stats = useMemo(() => {
    return [
      { label: "Total Files",  value: forms.length, icon: "📄", color: "#6366f1" },
      { label: "Admission",    value: forms.filter(f => f.Category === "Admission").length, icon: "🎓", color: "#22c55e" },
      { label: "Examination",  value: forms.filter(f => f.Category === "Examination").length, icon: "📝", color: "#f59e0b" },
      { label: "Important",    value: forms.filter(f => f.Mark_as_Important).length, icon: "⚠️", color: "#ef4444" },
    ];
  }, [forms]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="fg-page">
      {/* ── Loading Overlay ── */}
      {loading && (
        <div className="fg-loading-overlay">
          <div className="fg-spinner" />
          <p>Processing request...</p>
        </div>
      )}

      {/* ── Header ── */}
      <div className="fg-header">
        <div className="fg-header-left">
          <div className="fg-header-icon">📂</div>
          <div>
            <div className="fg-breadcrumb">
              <span>Media & Contact</span>
              <span>/</span>
              <span>Careers</span>
              <span>/</span>
              <span className="fg-bc-active">Forms & Guidelines</span>
            </div>
            <h1>Forms & Guidelines</h1>
            <p>Upload, manage, and publish downloadable application forms and policy documents.</p>
          </div>
        </div>
        <div className="fg-header-actions">
          <button className="fg-btn-primary" onClick={openAdd}>+ Add Document</button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="fg-stats-row">
        {stats.map((s, i) => (
          <div key={i} className="fg-stat-card" style={{ borderTopColor: s.color }}>
            <span className="fg-stat-icon">{s.icon}</span>
            <div>
              <strong>{s.value}</strong>
              <p>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tabs & Filters ── */}
      <div className="fg-tabs-wrap">
        <div className="fg-tabs">
          {TABS.map(t => (
            <button 
              key={t.key} 
              className={`fg-tab ${activeTab === t.key ? "active" : ""}`}
              onClick={() => setActiveTab(t.key)}
            >
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </div>
        
        <div className="fg-filters">
          <div className="fg-search">
            <span>🔍</span>
            <input 
              type="text" 
              placeholder="Search by title or category..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && <button className="fg-clr" onClick={() => setSearch("")}>×</button>}
          </div>
        </div>
      </div>

      {/* ── Content Grid ── */}
      <div className="fg-forms-grid">
        {filtered.length > 0 ? (
          filtered.map(item => (
            <div key={item._id} className={`fg-form-card ${item.Pin_to_Top ? "pinned-card" : ""} ${item.Status === "Inactive" ? "inactive-card" : ""}`}>
              <div className="fg-card-top">
                <div className="fg-file-icon">
                  <span>📄</span>
                  <span className="fg-file-badge">{item.File_Type}</span>
                </div>
                <div className="fg-card-badges">
                  {item.Mark_as_Important && <span className="fg-imp-badge">Important</span>}
                  {item.Pin_to_Top && <span className="fg-pin-badge">Pinned</span>}
                  <span className={`fg-status-badge ${item.Status.toLowerCase()}`}>{item.Status}</span>
                </div>
                <button className="fg-dots" onClick={(e) => openDD(e, item._id)}>⋮</button>
              </div>

              <h3 className="fg-card-title" onClick={() => openView(item)}>{item.Form_Title}</h3>
              
              <div className="fg-card-meta">
                <span>📂 {item.Category}</span>
                <span>👥 {item.For_Audience}</span>
                <span>📅 {item.Academic_Year}</span>
              </div>

              <p className="fg-card-desc">{item.Description || "No description provided."}</p>

              <div className="fg-card-footer">
                <div className="fg-card-footer-left">
                  <span className="fg-updated">Updated: {formatDate(item.Last_Updated)}</span>
                  <span className="fg-dl-count">📦 {item.File_Size || "N/A"}</span>
                </div>
                <div className="fg-card-footer-right">
                  <button className="fg-view-btn" onClick={() => openView(item)}>Details →</button>
                  {item.File_URL && (
                    <a href={item.File_URL} target="_blank" rel="noreferrer" className="fg-download-btn">
                      Download
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="fg-empty">
            <div>📭</div>
            <h3>No documents found</h3>
            <p>Try adjusting your search or category filters.</p>
          </div>
        )}
      </div>

      {/* ── Dropdown Menu ── */}
      {dropdown.id && (
        <div ref={dropdownRef} className="fg-dropdown" style={{ position: "fixed", top: dropdown.top, left: dropdown.left }}>
          <button onClick={() => openEdit(forms.find(f => f._id === dropdown.id))}>✏️ Edit Document</button>
          <button onClick={() => openView(forms.find(f => f._id === dropdown.id))}>👁️ View Details</button>
          <div className="fg-dd-divider" />
          <button className="fg-dd-del" onClick={() => { setConfirmDelete(dropdown.id); closeDD(); }}>🗑️ Delete Permanent</button>
        </div>
      )}

      {/* ── View Modal ── */}
      {modal === "view" && viewItem && (
        <div className="fg-overlay" onClick={() => setModal("")}>
          <div className="fg-view-modal" onClick={e => e.stopPropagation()}>
            <div className="fg-view-head">
              <div className="fg-view-icon">📄</div>
              <div>
                <h2>{viewItem.Form_Title}</h2>
                <div className="fg-card-meta">
                  <span className="fg-cat-pill">{viewItem.Category}</span>
                  <span>v{viewItem.Version}</span>
                </div>
              </div>
              <button className="fg-modal-close" onClick={() => setModal("")}>×</button>
            </div>
            <div className="fg-view-body">
              <div className="fg-view-section">
                <h4>Description</h4>
                <p>{viewItem.Description || "No description provided."}</p>
              </div>
              <div className="fg-view-details-grid">
                <div className="fg-detail-item"><span>Target Audience</span><strong>{viewItem.For_Audience}</strong></div>
                <div className="fg-detail-item"><span>Academic Year</span><strong>{viewItem.Academic_Year}</strong></div>
                <div className="fg-detail-item"><span>File Type</span><strong>{viewItem.File_Type}</strong></div>
                <div className="fg-detail-item"><span>File Size</span><strong>{viewItem.File_Size}</strong></div>
                <div className="fg-detail-item"><span>Last Updated</span><strong>{formatDate(viewItem.Last_Updated)}</strong></div>
                <div className="fg-detail-item"><span>Status</span><strong>{viewItem.Status}</strong></div>
              </div>
              <div className="fg-view-actions">
                <button className="fg-btn-secondary" onClick={() => openEdit(viewItem)}>Edit Content</button>
                {viewItem.File_URL && (
                  <a href={viewItem.File_URL} target="_blank" rel="noreferrer" className="fg-btn-primary">
                    Download File 📥
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Form Modal (Add/Edit) ── */}
      {(modal === "add" || modal === "edit") && (
        <div className="fg-overlay" onClick={() => setModal("")}>
          <div className="fg-form-modal" onClick={e => e.stopPropagation()}>
            <div className="fg-modal-head">
              <div>
                <h2>{modal === "add" ? "Add New Document" : "Edit Document"}</h2>
                <p>Fill in the details below to publish the document to the college website.</p>
              </div>
              <button className="fg-modal-close" onClick={() => setModal("")}>×</button>
            </div>
            <form className="fg-form" onSubmit={handleSave}>
              <div className="fg-field full">
                <label>Document Title <span className="req">*</span></label>
                <input 
                  type="text" 
                  required 
                  value={form.Form_Title} 
                  onChange={e => setForm({...form, Form_Title: e.target.value})}
                  placeholder="e.g. Admission Application Form 2025"
                />
              </div>

              <div className="fg-field-row">
                <div className="fg-field">
                  <label>Category <span className="req">*</span></label>
                  <select value={form.Category} onChange={e => setForm({...form, Category: e.target.value})}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="fg-field">
                  <label>Audience <span className="req">*</span></label>
                  <select value={form.For_Audience} onChange={e => setForm({...form, For_Audience: e.target.value})}>
                    {AUDIENCES.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>

              <div className="fg-field full">
                <label>Description</label>
                <textarea 
                  rows="3" 
                  value={form.Description} 
                  onChange={e => setForm({...form, Description: e.target.value})}
                  placeholder="Brief summary of the document contents..."
                />
              </div>

              <div className="fg-field-row">
                <div className="fg-field">
                  <label>File URL <span className="req">*</span></label>
                  <input 
                    type="url" 
                    required 
                    value={form.File_URL} 
                    onChange={e => setForm({...form, File_URL: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                <div className="fg-field">
                  <label>File Type</label>
                  <select value={form.File_Type} onChange={e => setForm({...form, File_Type: e.target.value})}>
                    <option value="PDF">PDF Document</option>
                    <option value="DOCX">Word Document</option>
                    <option value="XLSX">Excel Spreadsheet</option>
                    <option value="ZIP">Archive (ZIP)</option>
                  </select>
                </div>
              </div>

              <div className="fg-field-row">
                <div className="fg-field">
                  <label>File Size</label>
                  <input 
                    type="text" 
                    value={form.File_Size} 
                    onChange={e => setForm({...form, File_Size: e.target.value})}
                    placeholder="e.g. 2.4 MB"
                  />
                </div>
                <div className="fg-field">
                  <label>Academic Year</label>
                  <input 
                    type="text" 
                    value={form.Academic_Year} 
                    onChange={e => setForm({...form, Academic_Year: e.target.value})}
                    placeholder="e.g. 2025-26"
                  />
                </div>
              </div>

              <div className="fg-field-row">
                <div className="fg-field">
                  <label>Version</label>
                  <input 
                    type="text" 
                    value={form.Version} 
                    onChange={e => setForm({...form, Version: e.target.value})}
                  />
                </div>
                <div className="fg-field">
                  <label>Status</label>
                  <select value={form.Status} onChange={e => setForm({...form, Status: e.target.value})}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="fg-toggle-row">
                <label className="fg-toggle-item">
                  <input type="checkbox" checked={form.Pin_to_Top} onChange={e => setForm({...form, Pin_to_Top: e.target.checked})} />
                  <div className={`fg-chk-box ${form.Pin_to_Top ? "checked" : ""}`} />
                  <div><strong>Pin to Top</strong><p>Featured documents will stay at the top.</p></div>
                </label>
                <label className="fg-toggle-item">
                  <input type="checkbox" checked={form.Mark_as_Important} onChange={e => setForm({...form, Mark_as_Important: e.target.checked})} />
                  <div className={`fg-chk-box ${form.Mark_as_Important ? "checked" : ""}`} />
                  <div><strong>Mark as Important</strong><p>Adds a red warning badge to the document.</p></div>
                </label>
              </div>

              <div className="fg-form-footer">
                <button type="button" className="fg-btn-light" onClick={() => setModal("")}>Cancel</button>
                <button type="submit" className="fg-btn-primary">Save Document</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirm Delete ── */}
      {confirmDelete && (
        <div className="fg-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="fg-confirm" onClick={e => e.stopPropagation()}>
            <div className="fg-confirm-icon">⚠️</div>
            <h3>Delete Document?</h3>
            <p>This action is permanent and will remove the document link from the public website.</p>
            <div className="fg-confirm-actions">
              <button className="fg-btn-light" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="fg-btn-danger" onClick={() => handleDelete(confirmDelete)}>Delete Permanent</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormsGuidelines;